import * as React from 'react';
import { type Theme } from '@glitz/core';
import { styled, type StyledDecorator } from '@glitz/react';
import { isIOS } from '@avensia/excite';

export const FieldSize = {
  XLarge: 'xlarge',
  Large: 'large',
  Default: 'default',
  Small: 'small',
  XSmall: 'xsmall',
  XXSmall: 'xxsmall',
} as const;

type ValueOf<T> = T[keyof T];
export type FieldSizeType = Partial<ValueOf<typeof FieldSize>>;

const Context = React.createContext<ValueOf<typeof FieldSize>>(FieldSize.Default);

export function useFieldSize() {
  return React.useContext(Context);
}

function createFieldSizeProvider(size: ValueOf<typeof FieldSize>) {
  return function FieldSizeProvider(props: React.PropsWithChildren<{}>) {
    return <Context.Provider value={size}>{props.children}</Context.Provider>;
  };
}
export const XXSmallField = createFieldSizeProvider(FieldSize.XXSmall);
export const XSmallField = createFieldSizeProvider(FieldSize.XSmall);
export const SmallField = createFieldSizeProvider(FieldSize.Small);
export const XLargeField = createFieldSizeProvider(FieldSize.XLarge);
export const LargeField = createFieldSizeProvider(FieldSize.Large);

export function createFieldSizeDecorator(size: ValueOf<typeof FieldSize>, square = false, noPadding = false) {
  return styled({
    padding: {
      x: noPadding ? 0 : fieldXSpacing(size, square),
      y: noPadding ? 0 : fieldYSpacing(size),
    },
    fontSize: fieldFontSize(size),
    lineHeight: '24px',
  });
}

export function fieldXSpacing(size: ValueOf<typeof FieldSize>, square = false): (theme: Theme) => number {
  if (square) {
    return fieldYSpacing(size);
  }

  return () => {
    switch (size) {
      // -1px for the border
      case FieldSize.XXSmall:
        return 3; //t.sharedSpacing.xxs - 1;
      case FieldSize.XSmall:
        return 11; //t.sharedSpacing.s - 1;
      case FieldSize.Small:
        return 23; //t.sharedSpacing.l - 1;
      case FieldSize.Default:
        return 23; //t.sharedSpacing.l - 1;
      case FieldSize.Large:
        return 31; //t.sharedSpacing.xl -1;
      case FieldSize.XLarge:
        return 39; //t.sharedSpacing.xxl - 1;
    }
  };
}

export function fieldYSpacing(size: ValueOf<typeof FieldSize>): (theme: Theme) => number {
  return () => {
    switch (size) {
      // -1px for the border
      case FieldSize.XXSmall:
        return 0;
      case FieldSize.XSmall:
        return 3; //t.sharedSpacing.xxs -1;
      case FieldSize.Small:
        return 7; // t.sharedSpacing.xs - 1;
      case FieldSize.Default:
        return 11; //t.sharedSpacing.s - 1;
      case FieldSize.Large:
        return 15; //t.sharedSpacing.m - 1;
      case FieldSize.XLarge:
        return 23; //t.sharedSpacing.l - 1;
    }
  };
}

export const placeholderYSpacing = (size: ValueOf<typeof FieldSize>): StyledDecorator => {
  switch (size) {
    case FieldSize.XXSmall:
      return styled({ paddingTop: 7, paddingBottom: 1 });
    case FieldSize.XSmall:
      return styled({ paddingTop: 14, paddingBottom: 2 });
    case FieldSize.Small:
      return styled({ paddingTop: 12, paddingBottom: 2 });
    case FieldSize.Default:
      return styled({ paddingTop: t => t.sharedSpacing.m, paddingBottom: 6 });
    case FieldSize.Large:
      return styled({ paddingTop: 22, paddingBottom: 8 });
    case FieldSize.XLarge:
      return styled({ paddingTop: 32, paddingBottom: 14 });
  }
};

export function placeholderTopPosition(size: ValueOf<typeof FieldSize>): (theme: Theme) => number {
  return t => {
    switch (size) {
      case FieldSize.XXSmall:
        return 1;
      case FieldSize.XSmall:
        return 2;
      case FieldSize.Small:
        return 2;
      case FieldSize.Default:
        return t.sharedSpacing.xxs;
      case FieldSize.Large:
        return t.sharedSpacing.xxs;
      case FieldSize.XLarge:
        return t.sharedSpacing.s;
    }
  };
}

export function fieldFontSize(size: ValueOf<typeof FieldSize>): (theme: Theme) => number {
  return t => {
    let fontSize: number;

    switch (size) {
      case FieldSize.XXSmall:
        fontSize = t.sharedFontSize.xxs;
        break;
      case FieldSize.XSmall:
        fontSize = t.sharedFontSize.xxs;
        break;
      case FieldSize.Small:
        fontSize = t.sharedFontSize.xs;
        break;
      case FieldSize.Default:
        fontSize = t.sharedFontSize.s;
        break;
      case FieldSize.Large:
        fontSize = t.sharedFontSize.m;
        break;
      case FieldSize.XLarge:
        fontSize = t.sharedFontSize.m;
        break;
    }

    // We set font size to 16px because if it's less, iOS will zoom on input field
    return fontSize < 16 && isIOS() ? 16 : fontSize;
  };
}
